/**
 * SPKI-Fingerprint-Referenzimplementierung.
 *
 * Vertrag (siehe AFO-002):
 *   1. Eingang: SPKI-Public-Key im Standard-Base64-Format.
 *   2. Decode Base64 → DER-Bytes.
 *   3. `crypto.subtle.importKey('spki', ..., RSA-OAEP/SHA-256)` — kanonisiert ASN.1.
 *   4. Modulus-Check: `algorithm.modulusLength >= MIN_TENANT_KEY_MODULUS_BITS`.
 *   5. `crypto.subtle.exportKey('spki', key)` — kanonische SPKI-Bytes.
 *   6. `crypto.subtle.digest('SHA-256', ...)` über die kanonische Form.
 *   7. Erste 8 Bytes als 16 Lowercase-Hex-Zeichen ausgeben.
 *
 * Cross-Runtime: Node 22 (`globalThis.crypto.subtle`) und alle modernen Browser.
 * `extractable: true` ist Pflicht, weil wir den Schlüssel sofort wieder
 * exportieren — er wird unmittelbar verworfen.
 *
 * @see tickets/EPIC-001-patient-terminabsage/AFO-002-shared-vertrag/afo.md
 */

import { MIN_TENANT_KEY_MODULUS_BITS, SPKI_FINGERPRINT_BYTES } from './crypto.constants.js';
import { FingerprintError } from './fingerprint-error.js';
import { bytesToHexLower } from './hex.js';
import { base64ToBytes } from './safe-base64-std.js';

const IMPORT_ALGORITHM: RsaHashedImportParams = {
  name: 'RSA-OAEP',
  hash: 'SHA-256',
};

const IMPORT_KEY_USAGES: readonly KeyUsage[] = ['wrapKey'];

/**
 * Erzeugt die rohen Fingerprint-Bytes (erste `SPKI_FINGERPRINT_BYTES` Bytes
 * des kanonischen SHA-256-Digests). Diese Form wird in den Cancel-Token
 * geschrieben, siehe `docs/cancel-token-wire-format.md`.
 *
 * @param spkiBase64 Standard-Base64-kodierter SPKI-Public-Key (mit oder ohne Padding).
 * @returns Fingerprint als `SPKI_FINGERPRINT_BYTES`-langer Uint8Array (eigener Buffer).
 * @throws {FingerprintError} `INVALID_BASE64`, `KEY_IMPORT_FAILED` oder `KEY_TOO_WEAK`.
 */
export async function spkiFingerprintBytes(spkiBase64: string): Promise<Uint8Array> {
  const inputBytes = decodeSpkiInput(spkiBase64);
  const importedKey = await importPublicKey(inputBytes);
  ensureSufficientModulus(importedKey);
  const canonicalSpki = await exportCanonicalSpki(importedKey);
  const digest = await crypto.subtle.digest('SHA-256', canonicalSpki);
  return new Uint8Array(digest).slice(0, SPKI_FINGERPRINT_BYTES);
}

/**
 * Erzeugt den Lowercase-Hex-Fingerprint eines SPKI-Public-Keys
 * (`SPKI_FINGERPRINT_LENGTH` Zeichen). Wird im Pinning-Vergleich gegen den
 * dekodierten Token-Fingerprint genutzt.
 *
 * @param spkiBase64 Standard-Base64-kodierter SPKI-Public-Key (mit oder ohne Padding).
 * @returns Fingerprint als Hex-String, Lowercase, ohne Trennzeichen.
 * @throws {FingerprintError} `INVALID_BASE64`, `KEY_IMPORT_FAILED` oder `KEY_TOO_WEAK`.
 */
export async function spkiFingerprint(spkiBase64: string): Promise<string> {
  return bytesToHexLower(await spkiFingerprintBytes(spkiBase64));
}

function decodeSpkiInput(spkiBase64: string): Uint8Array {
  try {
    return base64ToBytes(spkiBase64);
  } catch (error) {
    throw new FingerprintError(
      'INVALID_BASE64',
      `SPKI base64 decode failed: ${describeError(error)}`,
    );
  }
}

async function importPublicKey(spkiBytes: Uint8Array): Promise<CryptoKey> {
  try {
    return await crypto.subtle.importKey('spki', spkiBytes, IMPORT_ALGORITHM, true, [
      ...IMPORT_KEY_USAGES,
    ]);
  } catch (error) {
    throw new FingerprintError('KEY_IMPORT_FAILED', `SPKI import failed: ${describeError(error)}`);
  }
}

function ensureSufficientModulus(key: CryptoKey): void {
  const algorithm = key.algorithm as RsaKeyAlgorithm;
  const modulusLength = algorithm.modulusLength;
  if (typeof modulusLength !== 'number' || modulusLength < MIN_TENANT_KEY_MODULUS_BITS) {
    throw new FingerprintError(
      'KEY_TOO_WEAK',
      `RSA modulus length ${String(modulusLength)} < required ${MIN_TENANT_KEY_MODULUS_BITS.toString()}`,
    );
  }
}

async function exportCanonicalSpki(key: CryptoKey): Promise<ArrayBuffer> {
  try {
    return await crypto.subtle.exportKey('spki', key);
  } catch (error) {
    throw new FingerprintError('KEY_IMPORT_FAILED', `SPKI export failed: ${describeError(error)}`);
  }
}

function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
